/**
 * Definitions page (S4) — the registry rendered as a navigable docs section. On
 * screen: a sticky pillar table-of-contents + filter (DefinitionsNav) beside the
 * catalogue, each entry reading as a manual page (overview → calculation → data
 * source & lineage). Composition only (CD-7): useMetrics owns GET /api/v1/metrics,
 * kit renders typed rows, the content layer adds the prose. The print stylesheet
 * still emits ONE artefact — nav/filter/print button print-hidden, the weekly
 * catalogue on a fresh page, index.css forcing black-on-white, and the filter's
 * `hidden` rows `print:block` so a filtered screen prints the whole catalogue.
 * States are honest: an API error is a visible message, never a blank document.
 */

import React from 'react'
import { AlertCircle } from 'lucide-react'
import InfoBanner from '../components/InfoBanner'
import DefinitionsCatalogue from '../components/kit/DefinitionsCatalogue'
import DefinitionsNav, { type NavChapter } from '../components/kit/DefinitionsNav'
import {
  monthlyCatalogueRows,
  pillarGroups,
  weeklyCatalogueRows,
} from '../components/kit/catalogueRows'
import { useMetrics } from '../hooks/useMetrics'
import type { Cadence, CatalogueResponse, RegistryMetric } from '../types/metrics'

function LoadingState() {
  return (
    <div
      role="status"
      className="animate-pulse rounded-lg border border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-400 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-500"
    >
      Loading definitions…
    </div>
  )
}

/** The pillars present in a catalogue, first-seen order, deduped — the nav TOC
 *  for that chapter (a pillar's served-component run reuses its first anchor). */
function chapterFor(cadence: Cadence, title: string, rows: RegistryMetric[]): NavChapter {
  const pillars = [...new Set(pillarGroups(rows).map((group) => group.pillar))]
  return { cadence, title, pillars }
}

/** The two catalogue chapters (Monthly, Weekly) — the docs body / print pages. */
function CatalogueColumn({
  monthly,
  weekly,
  query,
}: {
  monthly: RegistryMetric[]
  weekly: RegistryMetric[]
  query: string
}) {
  const components = monthly.filter((metric) => !metric.matrix).length
  return (
    <div className="min-w-0 space-y-8">
      <DefinitionsCatalogue
        testId="catalogue-monthly"
        title="Monthly catalogue"
        subtitle={`${monthly.length - components} matrix rows + ${components} served components · pillar order`}
        cadence="monthly"
        rows={monthly}
        query={query}
      />
      <DefinitionsCatalogue
        testId="catalogue-weekly"
        title="Weekly catalogue"
        subtitle={`${weekly.length} weekly rows · Kirkstall/Oracle only — the weekly master serves no Group rows`}
        cadence="weekly"
        rows={weekly}
        query={query}
        printBreakBefore
      />
    </div>
  )
}

function CatalogueSections({
  catalogue,
  query,
  onQueryChange,
}: {
  catalogue: CatalogueResponse
  query: string
  onQueryChange: (value: string) => void
}) {
  const monthly = monthlyCatalogueRows(catalogue.pillars)
  const weekly = weeklyCatalogueRows(catalogue.pillars)
  const chapters = [
    chapterFor('monthly', 'Monthly catalogue', monthly),
    chapterFor('weekly', 'Weekly catalogue', weekly),
  ]
  return (
    <div className="lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-8 print:block">
      <DefinitionsNav chapters={chapters} query={query} onQueryChange={onQueryChange} />
      <CatalogueColumn monthly={monthly} weekly={weekly} query={query} />
    </div>
  )
}

function DefinitionsBody({
  query,
  onQueryChange,
}: {
  query: string
  onQueryChange: (value: string) => void
}) {
  const catalogue = useMetrics()
  if (catalogue.error) {
    return (
      <InfoBanner
        variant="error"
        icon={AlertCircle}
        title="Definitions unavailable"
        message={catalogue.error.message}
      />
    )
  }
  if (!catalogue.data) return <LoadingState />
  return (
    <CatalogueSections catalogue={catalogue.data} query={query} onQueryChange={onQueryChange} />
  )
}

export default function Definitions(): React.JSX.Element {
  const [query, setQuery] = React.useState('')
  return (
    <div className="print-doc mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:px-8 print:max-w-none print:space-y-5 print:p-0">
      <DefinitionsBody query={query} onQueryChange={setQuery} />
    </div>
  )
}
